import * as React from "react";
import { cn } from "@/lib/utils";
import { 
  applyPhoneMask, 
  cleanPhone, 
  validateBrazilianPhone, 
  getPhonePlaceholder
} from "@/lib/phone-mask";

interface PhoneInputProps extends Omit<React.ComponentProps<"input">, "onChange" | "value"> {
  value?: string;
  onChange?: (value: string) => void;
  onRawChange?: (rawValue: string) => void; // For getting unmasked value
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, value = "", onChange, onRawChange, placeholder, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState(() => applyPhoneMask(value));
    const inputRef = React.useRef<HTMLInputElement>(null);
    
    // Sync with external value changes
    React.useEffect(() => {
      setInternalValue(applyPhoneMask(value));
    }, [value]);
    
    // Merge refs
    React.useImperativeHandle(ref, () => inputRef.current!, []);
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const cursorPosition = e.target.selectionStart || 0;
      
      // Apply mask
      const maskedValue = applyPhoneMask(inputValue);
      const rawValue = cleanPhone(inputValue);
      
      // Update internal state
      setInternalValue(maskedValue);
      
      // Calculate new cursor position
      const newCursorPosition = calculateCursorPosition(inputValue, maskedValue, cursorPosition);
      
      // Set cursor position after React updates
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      }, 0);
      
      // Call callbacks
      onChange?.(maskedValue);
      onRawChange?.(rawValue);
    };
    
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow navigation and special keys
      if (
        e.key === 'Backspace' ||
        e.key === 'Delete' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'Tab' ||
        e.key === 'Enter' ||
        e.ctrlKey ||
        e.metaKey
      ) {
        return;
      }
      
      // Only allow digits
      if (!/\d/.test(e.key)) {
        e.preventDefault();
      }
      
      // Limit to 11 digits (Brazilian mobile)
      const currentDigits = cleanPhone(internalValue);
      if (currentDigits.length >= 11 && /\d/.test(e.key)) {
        e.preventDefault();
      }
    };
    
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      
      const pastedText = e.clipboardData.getData('text');
      const digits = cleanPhone(pastedText);
      
      if (digits.length <= 11) {
        const maskedValue = applyPhoneMask(digits);
        setInternalValue(maskedValue);
        onChange?.(maskedValue);
        onRawChange?.(digits);
      }
    };
    
    // Determine validation state
    const isValid = !internalValue || validateBrazilianPhone(internalValue);
    const dynamicPlaceholder = placeholder || getPhonePlaceholder(internalValue);
    
    return (
      <input
        ref={inputRef}
        type="text"
        className={cn(
          "flex h-10 w-full rounded-md border-[0.5px] border-input bg-input-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          !isValid && internalValue && "border-destructive focus-visible:ring-destructive",
          className
        )}
        value={internalValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={dynamicPlaceholder}
        {...props}
      />
    );
  }
);

PhoneInput.displayName = "PhoneInput";

// Helper function to calculate cursor position after masking
function calculateCursorPosition(
  originalValue: string, 
  maskedValue: string, 
  originalPosition: number
): number {
  if (originalPosition === 0) return 0;
  if (originalPosition >= maskedValue.length) return maskedValue.length;
  
  let digitsSeen = 0;
  let targetDigits = 0;
  
  // Count digits up to cursor position in original value
  for (let i = 0; i < Math.min(originalPosition, originalValue.length); i++) {
    if (/\d/.test(originalValue[i])) {
      targetDigits++;
    }
  }
  
  // Find position in masked value where we have the same number of digits
  for (let i = 0; i < maskedValue.length; i++) {
    if (/\d/.test(maskedValue[i])) {
      digitsSeen++;
      if (digitsSeen === targetDigits) {
        return i + 1;
      }
    }
  }
  
  return maskedValue.length;
}

export { PhoneInput };